import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { RootStateAuth } from '../../store/authReducer';
import { ConfirmarNovaSenha, ValidarTokenSenha } from '../../store/confirmarSenha/actions/confirmarSenhaAction';
import ConfirmarSenhaContainer from '../../containers/confirmarSenha/confirmarSenhaContainer';
import * as S from './confirmarSenhaStyle';
import { Forms } from 'base/src/components/Form';
import { Input } from 'base/src/components/Input';
import { Redirect, RouteParamters } from 'base/src/utils/routeUtil';
import { GetAuthRoute } from 'domain/types/sistema/sistemaList';
import { Button } from 'base/src/components/Button';
import  StateMensage  from 'base/src/view/states/state';
import { CONFIRMAR_SENHA_VALIDAR } from '../../types/confirmarSenha/confirmarSenha';
import { View } from 'base/src/components/Basic/View/view';
import WdLogo from 'base/src/components/WDLogo';
import { TFunction } from 'i18next';
import { useTheme } from 'base/src/utils/context/themeContext';

// Password strength component
function PasswordStrengthIndicator (props : {strength: number, t: TFunction}) {
  const { theme } = useTheme();

  function getColor() {
    if (props.strength < 30) return theme.colors.negative; // Weak - Red
    if (props.strength < 70) return theme.colors.warning; // Medium - Orange
    return theme.colors.positive; // Strong - Green
  }
  
  function getMessage () {
    if (props.strength < 30) return props.t('confirmarSenha.forcaSenhaFraca');
    if (props.strength < 70) return props.t('confirmarSenha.forcaSenhaMedia');
    return props.t('confirmarSenha.forcaSenhaForte');
  }
  
  return (
    <div style={{ marginBottom: '15px' }}>
      <div style={{ 
        height: '5px', 
        width: '100%', 
        backgroundColor: '#ddd',
        borderRadius: '5px',
        marginTop: '5px'
      }}>
        <div style={{ 
          height: '100%', 
          width: `${props.strength}%`, 
          backgroundColor: getColor(),
          borderRadius: '5px',
          transition: 'width 0.3s, background-color 0.3s'
        }} />
      </div>
      <span style={{ 
        fontSize: '12px', 
        color: getColor(),
        marginTop: '2px',
        display: 'block'
      }}>
        {getMessage()}
      </span>
    </div>
  );
}

const connector = connectUtil(
  (state: RootStateAuth) => ({
    success: state.ConfirmarSenhaReducer?.success,
    tokenValid: state.ConfirmarSenhaReducer?.tokenValid,
  }),
  {  ConfirmarNovaSenha, ValidarTokenSenha }
);

export interface ConfirmarSenhaProps extends PropsFromRedux<typeof connector> { }

function ConfirmarSenha(props: ConfirmarSenhaProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  const container = new ConfirmarSenhaContainer(props);
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get(RouteParamters.token);

  useEffect(() => {
    if (tokenParam) {
      props.ValidarTokenSenha(tokenParam, Redirecionar)
    } else {
      Redirect(GetAuthRoute());
    }
  }, []);

  useEffect(() => {
    if (props.tokenValid == null) {
      return
    }
    if (tokenParam == null || props.tokenValid != true) {
      RedirecionarAuth();
    }
  }, [props.tokenValid])

  function RedirecionarAuth() {
      setTimeout(() => {
        Redirecionar()
      }, 3000);
  }

  function Redirecionar() {
      navigate('/');
  }

  useEffect(()=>{
    if(props.success?.IsValid == true && props.success.Result == false){
      RedirecionarAuth()
    }
  }, [props.success]);

  // Calculate password strength
  function calculatePasswordStrength (password: string): number  {
    if (!password) return 0;
    
    let strength = 0;
    
    // Length contribution (max 25 points)
    strength += Math.min(25, Math.floor(password.length * 3));
    

    if (/[a-z]/.test(password)) strength += 10; // lowercase
    if (/[A-Z]/.test(password)) strength += 15; // uppercase
    if (/[0-9]/.test(password)) strength += 15; // numbers
    if (/[^a-zA-Z0-9]/.test(password)) strength += 20; // special chars
    
    // Variety bonus
    const varietyCount = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/]
      .filter(regex => regex.test(password)).length;
    strength += (varietyCount - 1) * 5;
    
    return Math.min(100, strength);
  }

  function handleNovaSenhaChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newPassword = e.target.value;
    setNovaSenha(newPassword);
    setPasswordStrength(calculatePasswordStrength(newPassword));
    setError('');
  }

  function handleConfirmarSenhaChange(e: React.ChangeEvent<HTMLInputElement>) {
    setConfirmarSenha(e.target.value);
    setError('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (novaSenha !== confirmarSenha) {
      setError(t('confirmarSenha.senhasNaoConferem'));
      return;
    }


    tokenParam && await container.enviarNovaSenha(novaSenha, confirmarSenha, tokenParam);
  }

  useEffect(() => {
    if (props.success == null) { return }

    setNovaSenha("");
    setConfirmarSenha("")
   
  }, [props.success])

  return (
    <S.Container>
      <S.FormContainer>
    
        <View>
        <StateMensage stateErrorNames={[CONFIRMAR_SENHA_VALIDAR]} />

        <S.FormField style={{justifyContent:"center"}}><WdLogo></WdLogo></S.FormField>


        <S.Title>{t('confirmarSenha.titulo')}</S.Title>

        <S.Description>
          {t('confirmarSenha.instrucoes')}
        </S.Description>

        {props.success?.IsValid == true && (
          <S.SuccessMessage>
            {props.success.Message}
          </S.SuccessMessage>
        )}

        {props.success?.IsValid == false && (
          <S.ErrorMessage>
            {props.success.Message}
          </S.ErrorMessage>
        )}

        {error && (
          <S.ErrorMessage>
            {t(error)}
          </S.ErrorMessage>
        )}

        <Forms handleForm={handleSubmit}>
       
        <View>
          {novaSenha && <PasswordStrengthIndicator t={t} strength={passwordStrength} />}
          </View>

          <S.FormField>
            <Input
              type="password"
              label={t('confirmarSenha.novaSenha')}
              value={novaSenha}
              onChange={handleNovaSenhaChange}
              require
              disabled={!tokenParam || props.tokenValid == false || props.success?.IsValid}
            />
     
          </S.FormField>
      
          <S.FormField>
            <Input
              type="password"
              label={t('confirmarSenha.confirmarSenha')}
              value={confirmarSenha}
              onChange={handleConfirmarSenhaChange}
              require
              disabled={!tokenParam || props.tokenValid == false || props.success?.IsValid}
            />
          </S.FormField>

          <S.FormField>
            <Button
              size='lg'
              type="submit"
              disabled={!tokenParam || !novaSenha || !confirmarSenha || props.tokenValid == false || props.success?.IsValid}
            >
              {t('confirmarSenha.enviar')}
            </Button>
          </S.FormField>
        </Forms>
        </View>
      </S.FormContainer>
    </S.Container>
  );
}

export default connector(ConfirmarSenha);
